import React, { useState } from 'react'
import {
  Button,
  Col,
  Container,
  FormControl,
  InputGroup,
  Row
} from 'react-bootstrap'
import { useWizard } from 'react-use-wizard'
import { DocumentAddStep } from '../model'
import Icon from '@mdi/react'
import { mdiFilePdf, mdiFileWord, mdiFileImage, mdiFileDocument } from '@mdi/js'
import { getFileNameExtension } from 'share/utils/fileUtils'
import '../styles.scss'

interface StepEditFileProps {
  file: File | undefined
  fileIndex: number
  subcategoryName: string
  subcategoryDescription: string
  onEditFinished: (newFileName: string, fileIndex: number) => void
  onDeleteFile: (index?: number) => void
}

const StepEditFile = (props: StepEditFileProps): JSX.Element => {
  const { previousStep, nextStep } = useWizard()
  const [newFilename, setNewFilename] = useState(
    getFileNameExtension(props.file?.name ?? '')[0]
  )
  const [invalidFileNameErrorMessage, setInvalidFileNameErrorMessage] =
    useState<string | null>(null)

  if (!props.file) {
    previousStep(DocumentAddStep.Start)
    return <></>
  }

  function onSetNewFileName(newFileName: string) {
    setNewFilename(newFileName)
    setInvalidFileNameErrorMessage(null)
  }

  function isStringEmptyOrWhitespace(str: string) {
    // Check for null or undefined first to avoid errors when calling .trim()
    if (str === null || str === undefined) {
      return true
    }

    // Trim whitespace from both ends and check if the length is 0
    return str.trim().length === 0
  }

  const renderFailViolator = () => {
    return (
      <p className='text-danger'>
        <span>{invalidFileNameErrorMessage}</span>
      </p>
    )
  }

  const onContinueClick = () => {
    props.onEditFinished(newFilename, props.fileIndex)
    if (isStringEmptyOrWhitespace(newFilename)) {
      setInvalidFileNameErrorMessage('File name cannot be blank')
    } else {
      nextStep(DocumentAddStep.SubmitFiles)
    }
  }

  const onBackClick = () => {
    props.onDeleteFile(props.fileIndex)
    previousStep(DocumentAddStep.Start)
  }

  const getIcon = (filename: string): string => {
    const extension = filename.split('.').pop()
    switch (extension) {
      case 'pdf':
        return mdiFilePdf
      case 'doc':
      case 'docx':
        return mdiFileWord
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'png':
      case 'bmp':
        return mdiFileImage
      default:
        return mdiFileDocument
    }
  }

  return (
    <React.Fragment>
      <Container>
        <Row>
          <Col xs>
            <h2>{props.subcategoryName}</h2>
            <p>{props.subcategoryDescription}</p>
          </Col>
        </Row>
        <Row>
          <Col xs>
            <p>
              <strong>Document Name</strong>
            </p>
            <InputGroup>
              <InputGroup.Prepend>
                <div className='mx-2 my-1'>
                  <div className='mx-auto align-middle'>
                    <Icon path={getIcon(props.file.name)} size={1} />
                  </div>
                </div>
              </InputGroup.Prepend>
              <FormControl
                aria-describedby='basic-addon1'
                value={newFilename}
                onChange={(e) => onSetNewFileName(e.target.value)}
              />
              <InputGroup.Append>
                <InputGroup.Text>
                  {getFileNameExtension(props.file.name)[1]}
                </InputGroup.Text>
              </InputGroup.Append>
            </InputGroup>
          </Col>
        </Row>
        {invalidFileNameErrorMessage && renderFailViolator()}
      </Container>
      <div className='modal-footer modal-footer-custom mt-3'>
        <Button className='footer-btn' onClick={onContinueClick}>
          Continue
        </Button>
        <Button
          className='footer-btn btn-custom-secondary'
          variant='outline-secondary'
          onClick={onBackClick}
        >
          Back
        </Button>
      </div>
    </React.Fragment>
  )
}

export default StepEditFile
